import { useAuth } from './use-auth';
import { buildApiUrl } from '../lib/api';
import { supabase } from '../lib/supabaseClient';

/**
 * Hook that provides an authenticated request function with automatic:
 * - Authorization header from Supabase session
 * - X-Active-Tenant-Context header for SuperAdmin users
 * - Consistent error handling
 * - Built-in API URL construction
 */
export const useAuthenticatedRequest = () => {
  const { user } = useAuth();

  const authenticatedRequest = async (
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<Response> => {
    // Get session and validate access token
    const session = await supabase.auth.getSession();
    if (!session.data.session?.access_token) {
      throw new Error("No access token found for authenticated request");
    }

    // Build headers with authentication
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.data.session.access_token}`,
      ...options.headers as Record<string, string>
    };

    // Add tenant context header for SuperAdmin users
    if (user?.isSuperAdmin) {
      // Convert null to "null" string for the header
      const tenantContext = user.activeTenantContext === null ? "null" : (user.activeTenantContext || "__ALL__");
      headers['X-Active-Tenant-Context'] = tenantContext;
    }

    // Make the request
    const response = await fetch(buildApiUrl(endpoint), {
      ...options,
      headers
    });

    return response;
  };

  return authenticatedRequest;
}; 